import { User } from '@arpeggi/shared'
import Downshift from 'downshift'
import { useMemo, useState } from 'react'
import { useDebounce } from 'use-debounce'
import { styled } from '../../../lib/stitches'
import { useUsers } from '../../../queries/users'
import Avatar from '../../core/Avatar'
import { FieldErrorLabel } from '../../core/FieldErrorLabel'
import { FieldLabel } from '../../core/FieldLabel'
import { Flex } from '../../core/Flex'
import Text from '../../core/Text/Text'
import { TextInput } from '../../core/TextInput'

interface Props {
  onChange: (user: User) => void
  defaultUser?: User
  errorMessage?: string
}

export const StyledMenuSelect = styled('ul', {
  backgroundColor: 'White',
  boxShadow: '$big',
  listStyle: 'none',
  maxHeight: '300px',
  maxWidth: '400px',
  overflowY: 'scroll',
  paddingLeft: 0,
  marginBottom: 0
})

export const StyledMenuSelectItem = styled('li', {
  padding: '$10',
  transition: 'background-color 0.1s ease, color 0.1s ease',
  '@:hover': {
    cursor: 'pointer'
  },
  display: 'flex',
  alignItems: 'baseline',
  variants: {
    highlighted: {
      true: {
        backgroundColor: '$gray04'
      }
    },
    selected: {
      true: {
        fontWeight: '$bold'
      }
    }
  }
})

export const UserSelect: React.FC<Props> = ({ onChange, defaultUser, errorMessage }) => {
  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebounce(search, 250)
  const usersQuery = useUsers({ perPage: 20, search: debouncedSearch })

  const items = useMemo(
    () => usersQuery.data?.pages.flatMap((page) => page.data.map((user) => user)) || [],
    [usersQuery.data?.pages]
  )

  return (
    <Downshift
      initialSelectedItem={defaultUser}
      itemToString={(item) => (item ? item.displayName : '')}
      onInputValueChange={(inputValue) => setSearch(inputValue)}
      onChange={(user) => onChange(user)}
    >
      {({
        isOpen,
        getLabelProps,
        getMenuProps,
        getInputProps,
        highlightedIndex,
        getItemProps,
        selectedItem,
        clearSelection
      }) => (
        <div>
          <Flex direction="column">
            <FieldLabel {...getLabelProps()}>Choose a user</FieldLabel>
            {/* @ts-ignore */}
            <TextInput
              {...getInputProps({ onChange: clearSelection })}
              placeholder="Search users"
              css={{ maxWidth: '100%', width: '400px' }}
            />
            <StyledMenuSelect {...getMenuProps()}>
              {isOpen &&
                items.map((item, index) => {
                  const isSelected = selectedItem?.id === item.id

                  return (
                    <StyledMenuSelectItem
                      key={item.id}
                      {...getItemProps({ item, index })}
                      highlighted={highlightedIndex === index}
                      selected={isSelected}
                    >
                      <Text
                        size="14"
                        css={{ marginRight: '8px', fontWeight: isSelected ? '$bold' : 'initial' }}
                      >
                        {item.displayName}
                      </Text>
                      <Text size="10" type="secondary">
                        {' '}
                        ({item.walletAddress})
                      </Text>
                    </StyledMenuSelectItem>
                  )
                })}
            </StyledMenuSelect>
            {selectedItem && (
              <Flex css={{ gap: '$16', marginTop: '$10' }}>
                <Avatar imageUrl={selectedItem.profilePicture?.url} />
                <Flex direction="column">
                  <Text size="16">{selectedItem.displayName}</Text>
                  <Text size="12" css={{ color: '$gray08' }}>
                    {selectedItem.walletAddress}
                  </Text>
                </Flex>
              </Flex>
            )}
            <FieldErrorLabel>{errorMessage}</FieldErrorLabel>
          </Flex>
        </div>
      )}
    </Downshift>
  )
}
